// src/components/listing-leads/ListingLeadDrawer.jsx
// Listing lead detail panel. Restyled to mirror the Deals Board's detail view
// (wide, two-column, dark header) so clicking a pipeline card feels the same as
// clicking a board card — instead of the old narrow popup. Behaviour is
// unchanged: same actions (graduate / mark lost / delete / edit notes) and the
// same props (lead, canManage, onClose).
import React, { useEffect, useState } from "react";
import { X, Ban, CheckCircle2, Trash2, MoreVertical, MapPin, ExternalLink, FileText, Mail } from "lucide-react";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import AddClientPicker from "../new-contact/AddClientPicker";
import ContactEditDrawer from "../new-contact/ContactEditDrawer";
import {
  updateListingLead,
  convertListingLeadToDeal,
  deleteListingLead,
  getContactsByIds,
} from "../../utils/firebasefunctions";
import { addContact } from "../../utils/contactsApi";
import { normalizeContact } from "../../utils/contactNormalize";
import {
  listClients,
  addCoClient,
  removeClient,
  clientNames,
} from "../../utils/leadClients";
import {
  STAGE_COLORS,
  GRADUATION_STAGE,
  LISTING_STAGES,
  PARKING_STAGES,
} from "../../utils/listingLeadStages";
import {
  systemSourceOptions,
  leadSourceLabel,
  resolveSystemSource,
} from "../../utils/leadSourceLabel";
import EditableNotes from "../reuseable/EditableNotes";
import { notifyDealMilestone } from "../../utils/helper";
import { LISTING_FORM_TYPES } from "../../utils/portalForms";
import { requestSellerFile } from "../../utils/sellerFileRequest";
import { sendSellerPortalForms } from "../../utils/sendSellerPortalForms";
import { excludeTestAgents } from "../../utils/testAgents";

const fmtPrice = (n) => (n ? `$${Number(n).toLocaleString("en-US")}` : "");

// Coerce a possibly-object field into a safe display string. Some migrated
// leads stored the deal's leadsource/agent OBJECT where a string was expected;
// rendering that object directly throws React error #31 and blanks the page.
const text = (v) => {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number") return String(v);
  if (typeof v === "object")
    return v.name || [v.firstname, v.lastname].filter(Boolean).join(" ") || "";
  return "";
};

// Dates arrive as either epoch-ms (createdAt) or YYYY-MM-DD strings (appt /
// follow-up). Parse YYYY-MM-DD as LOCAL midnight so it doesn't slip a day.
const fmtDate = (v) => {
  if (v == null || v === "") return "";
  const s = String(v);
  let d;
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymd) d = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
  else if (/^\d+$/.test(s)) d = new Date(Number(s));
  else d = new Date(s);
  if (!d || isNaN(d.getTime())) return text(v);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const initialsOf = (name) =>
  (text(name) || "")
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const Row = ({ k, v }) => {
  const val = text(v);
  return val ? (
    <div className="flex justify-between py-2 border-b border-slate-100 last:border-0 text-sm">
      <span className="text-slate-500">{k}</span>
      <span className="text-slate-800 font-medium text-right">{val}</span>
    </div>
  ) : null;
};

// A date row that's an inline <input type="date"> when the viewer can manage
// the lead (saves immediately via onSave), and read-only formatted text
// otherwise. Used for the milestone dates the agent updates as the lead moves
// through the pipeline (CMA, listing appointment, next follow-up).
const EditableDateRow = ({ k, value, canEdit, onSave }) => {
  const [val, setVal] = useState(value || "");
  const [flash, setFlash] = useState(false);
  useEffect(() => setVal(value || ""), [value]);

  if (!canEdit) {
    const disp = fmtDate(value);
    return disp ? <Row k={k} v={disp} /> : null;
  }

  const handle = async (e) => {
    const next = e.target.value;
    const prev = val;
    setVal(next);
    const res = await onSave(next);
    if (res && res.success === false) {
      setVal(prev);
      toast.error("Couldn't save the date — try again");
    } else {
      setFlash(true);
      setTimeout(() => setFlash(false), 1200);
    }
  };

  return (
    <div className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0 text-sm gap-2">
      <span className="text-slate-500 flex items-center gap-1.5">
        {k}
        {flash && <span className="text-[10px] text-emerald-600">Saved ✓</span>}
      </span>
      <input
        type="date"
        value={val}
        onChange={handle}
        className="text-slate-800 font-medium bg-slate-50 border border-slate-200 rounded-md px-2 py-1 text-sm outline-none focus:border-[#1E293B]"
      />
    </div>
  );
};

// Parse a typed money/number string back into a stored number (or "" to clear).
const toNum = (v) => {
  const n = Number(String(v == null ? "" : v).replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : "";
};

// Inline-editable text/number row: an input when the viewer can manage the lead
// (saves on blur / Enter), read-only formatted text otherwise. Mirrors
// EditableDateRow. `display` formats the read-only value (e.g. money).
const EditableTextRow = ({ k, value, canEdit, onSave, type = "text", display, placeholder }) => {
  const [val, setVal] = useState(value == null ? "" : value);
  const [flash, setFlash] = useState(false);
  useEffect(() => setVal(value == null ? "" : value), [value]);

  if (!canEdit) {
    const shown = display ? display(value) : text(value);
    return shown ? <Row k={k} v={shown} /> : null;
  }

  const commit = async () => {
    // Skip a redundant write when the typed value normalizes to what's stored
    // (e.g. "$620,000" over a stored 620000) — avoids a wasted write + flicker.
    const same =
      type === "number"
        ? toNum(val) === toNum(value)
        : String(val) === String(value == null ? "" : value);
    if (same) return;
    const res = await onSave(val);
    if (res && res.success === false) {
      setVal(value == null ? "" : value);
      toast.error("Couldn't save — try again");
    } else {
      setFlash(true);
      setTimeout(() => setFlash(false), 1200);
    }
  };

  return (
    <div className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0 text-sm gap-2">
      <span className="text-slate-500 flex items-center gap-1.5 shrink-0">
        {k}
        {flash && <span className="text-[10px] text-emerald-600">Saved ✓</span>}
      </span>
      <input
        type="text"
        inputMode={type === "number" ? "decimal" : undefined}
        value={val}
        placeholder={placeholder}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
        className="text-slate-800 font-medium bg-slate-50 border border-slate-200 rounded-md px-2 py-1 text-sm outline-none focus:border-[#1E293B] text-right w-[58%]"
      />
    </div>
  );
};

// Inline-editable select row: a dropdown when manageable, read-only text
// otherwise. `options` is [{ value, label }]; `display` formats the read value.
const EditableSelectRow = ({ k, value, canEdit, onSave, options, display, placeholder = "—" }) => {
  const [flash, setFlash] = useState(false);

  if (!canEdit) {
    const shown = display ? display(value) : text(value);
    return shown ? <Row k={k} v={shown} /> : null;
  }

  const handle = async (e) => {
    const res = await onSave(e.target.value);
    if (res && res.success === false) {
      toast.error("Couldn't save — try again");
    } else {
      setFlash(true);
      setTimeout(() => setFlash(false), 1200);
    }
  };

  return (
    <div className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0 text-sm gap-2">
      <span className="text-slate-500 flex items-center gap-1.5 shrink-0">
        {k}
        {flash && <span className="text-[10px] text-emerald-600">Saved ✓</span>}
      </span>
      <select
        value={value == null ? "" : value}
        onChange={handle}
        className="text-slate-800 font-medium bg-slate-50 border border-slate-200 rounded-md px-2 py-1 text-sm outline-none focus:border-[#1E293B] max-w-[58%]"
      >
        <option value="">{placeholder}</option>
        {/* Keep the current value visible even if it's not in `options` (e.g. an
            assigned agent filtered out of the pool, or a legacy source). */}
        {value != null &&
          value !== "" &&
          !options.some((o) => String(o.value) === String(value)) && (
            <option value={value}>
              {(display ? display(value) : text(value)) || String(value)}
            </option>
          )}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
};

const Card = ({ label, children, className = "" }) => (
  <div
    className={`bg-white border border-slate-200 rounded-xl p-4 ${className}`}
  >
    {label && (
      <h4 className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-2">
        {label}
      </h4>
    )}
    {children}
  </div>
);

// Co-listing agents — a second (or more) agent on the listing. Both the main
// and the co-listing agents see and can work the lead. Saves immediately.
const CoListingAgentsRow = ({ lead, agents, canEdit }) => {
  const co = Array.isArray(lead.coAgents) ? lead.coAgents : [];
  if (!canEdit && co.length === 0) return null;
  const pool = excludeTestAgents(agents || []);
  return (
    <div className="py-2 border-b border-slate-100 last:border-0 text-sm">
      <div className="flex justify-between items-start gap-2">
        <span className="text-slate-500 pt-0.5">Co-Listing Agents</span>
        <div className="flex-1 flex flex-wrap gap-1.5 justify-end">
          {co.length === 0 ? (
            <span className="text-slate-400">—</span>
          ) : (
            co.map((c) => (
              <span
                key={c.id}
                className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 rounded-full px-2 py-0.5 text-xs font-medium"
              >
                {c.name || c.id}
                {canEdit && (
                  <button
                    type="button"
                    className="text-indigo-400 hover:text-indigo-700"
                    onClick={() =>
                      updateListingLead(lead.id, {
                        coAgents: co.filter((x) => x.id !== c.id),
                      })
                    }
                  >
                    ×
                  </button>
                )}
              </span>
            ))
          )}
        </div>
      </div>
      {canEdit && (
        <select
          className="mt-2 w-full text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white text-slate-700"
          value=""
          onChange={(e) => {
            const id = e.target.value;
            if (!id) return;
            const a = pool.find((x) => x.id === id);
            if (!a || co.some((x) => x.id === id)) return;
            const name =
              [a.firstname, a.lastname].filter(Boolean).join(" ") || a.name || a.id;
            updateListingLead(lead.id, { coAgents: [...co, { id, name }] });
          }}
        >
          <option value="">+ Add co-listing agent…</option>
          {pool
            .filter(
              (a) => a.id !== lead.assignedAgent && !co.some((c) => c.id === a.id),
            )
            .map((a) => (
              <option key={a.id} value={a.id}>
                {[a.firstname, a.lastname].filter(Boolean).join(" ") || a.name || a.id}
              </option>
            ))}
        </select>
      )}
    </div>
  );
};

export default function ListingLeadDrawer({ lead, canManage, onClose }) {
  const [{ user, users, employees, leadsource }] = useStateValue();
  const [busy, setBusy] = useState(false);
  // A file request fired from THIS drawer — keeps the button locked even if
  // the lead-side link write fails (the deal exists either way).
  const [requestedHere, setRequestedHere] = useState(false);
  const [formType, setFormType] = useState(lead.formType || "seller");
  const [menuOpen, setMenuOpen] = useState(false); // ⋯ menu (lost/delete)
  const [contact, setContact] = useState(null);
  const [coPickerOpen, setCoPickerOpen] = useState(false);
  const [creatingCoContact, setCreatingCoContact] = useState(false);
  const [primaryPickerOpen, setPrimaryPickerOpen] = useState(false);
  const [creatingPrimaryContact, setCreatingPrimaryContact] = useState(false);

  // Pull the linked contact so the Client card can show email / phone — the
  // lead itself only stores contactId + contactName.
  useEffect(() => {
    let alive = true;
    if (!lead?.contactId) {
      setContact(null);
      return;
    }
    getContactsByIds([lead.contactId])
      .then((rows) => alive && setContact(normalizeContact(rows?.[0])))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [lead?.contactId]);

  // --- Co-clients: add/remove on the saved lead (realtime listener refreshes it).
  const persistClients = (next) =>
    updateListingLead(lead.id, {
      contactId: next.contactId,
      contactName: next.contactName,
      coClients: next.coClients || [],
    });
  const addCoClientToLead = async (contactId) => {
    setCoPickerOpen(false);
    let name = "";
    try {
      name = normalizeContact((await getContactsByIds([contactId]))[0])?.name || "";
    } catch (_) {
      /* keep blank name */
    }
    await persistClients(addCoClient(lead, { contactId, contactName: name }));
  };
  const removeCoClient = (contactId) => persistClients(removeClient(lead, contactId));

  // Swap the primary client (seller). Keeps any co-clients; just replaces who's
  // first. The card/header read contactName, so denormalize the full name.
  const changePrimary = async (contactId) => {
    setPrimaryPickerOpen(false);
    let name = "";
    try {
      name = normalizeContact((await getContactsByIds([contactId]))[0])?.name || "";
    } catch (_) {
      /* keep blank name — still link the contactId */
    }
    return updateListingLead(lead.id, {
      contactId,
      contactName: name,
      // Don't let the new primary also linger as a co-client (would list the
      // same person twice).
      coClients: (Array.isArray(lead.coClients) ? lead.coClients : []).filter(
        (c) => c && c.contactId !== contactId,
      ),
    });
  };
  const handleCreatedPrimaryContact = async (form) => {
    if (!user?.gmailtoken?.access_token) {
      toast.error("Connect Gmail in Settings before adding a new contact");
      return;
    }
    try {
      const created = await addContact({ user, contact: form });
      setCreatingPrimaryContact(false);
      if (created?.firebaseId) await changePrimary(created.firebaseId);
    } catch (e) {
      toast.error(e.message || "Failed to create contact");
    }
  };
  const handleCreatedCoContact = async (form) => {
    if (!user?.gmailtoken?.access_token) {
      toast.error("Connect Gmail in Settings before adding a new contact");
      return;
    }
    try {
      const created = await addContact({ user, contact: form });
      setCreatingCoContact(false);
      if (created?.firebaseId) await addCoClientToLead(created.firebaseId);
    } catch (e) {
      toast.error(e.message || "Failed to create contact");
    }
  };

  const handleGraduate = async () => {
    setBusy(true);
    const res = await convertListingLeadToDeal(
      lead,
      {
        // Deals store the full system lead-source object; resolve the lead's
        // stored id (or legacy text) so commission tracking keeps working.
        leadsource:
          resolveSystemSource(lead.source, leadsource) || lead.source || "",
      },
      user,
    );
    setBusy(false);
    if (res.success) {
      notifyDealMilestone(
        {
          agentData: lead.assignedAgent,
          propertyaddress: lead.propertyAddress,
          createdat: res.dealId,
        },
        "Pre-Market",
        users,
        user,
      ).catch(() => {});
      toast.success("Listed! Moved to the Deals Board (Pre-Market).");
      onClose();
    } else toast.error(res.error || "Failed to graduate");
  };

  const handleMarkLost = async () => {
    const reason =
      window.prompt("Reason this listing is lost/inactive? (optional)") || "";
    setBusy(true);
    const res = await updateListingLead(lead.id, {
      status: "lost",
      lostReason: reason,
      lostAt: Date.now(),
    });
    setBusy(false);
    if (res.success) {
      toast.success("Marked as Lost");
      onClose();
    } else toast.error("Failed");
  };

  const handleDelete = async () => {
    const sure = window.confirm(
      `Delete ${lead.contactName || "this lead"} from the pipeline? This removes them from the board entirely.`,
    );
    if (!sure) return;
    setBusy(true);
    const res = await deleteListingLead(lead.id);
    setBusy(false);
    if (res.success) {
      toast.success("Lead deleted");
      onClose();
    } else toast.error("Failed to delete");
  };

  // Request a listing file for this already-existing lead — same flow as the
  // "Also Request a File" checkbox in NewListingLeadModal, just fired later
  // in the lead's life instead of at creation time.
  const handleRequestFile = async () => {
    // Entry guard: never double-create, even if a stale render left the
    // button clickable (e.g. another staffer just requested on this lead).
    if (busy || requestedHere || lead.sourceDealId || lead.fileRequested) return;
    setBusy(true);
    try {
      // requestSellerFile mints the file (Drive folder), links the lead, emails
      // recipients, and carries the chosen Form Type so the portal assembles the
      // right property-type forms. It's idempotent, so the drawer can lock after.
      await requestSellerFile(lead, user, { sendForms: false, formType });
      setRequestedHere(true);
      toast.success("Listing file requested");
    } catch (e) {
      console.error("Request file failed:", e);
      toast.error("Couldn't request the file — try again");
    } finally {
      setBusy(false);
    }
  };
  const handleSendSellerForms = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // Ensure a file exists (idempotent), then email the seller their forms.
      const { deal } = await requestSellerFile(lead, user, {
        sendForms: false,
        formType,
      });
      setRequestedHere(true);
      const sent = await sendSellerPortalForms(deal, lead, user);
      if (sent.ok) toast.success(sent.demo ? "Seller forms sent (demo)" : "Seller forms sent");
      else toast.error(`Couldn't send forms: ${sent.reason || "unknown error"}`);
    } catch (e) {
      console.error("Send seller forms failed:", e);
      toast.error("Failed to send seller forms");
    } finally {
      setBusy(false);
    }
  };

  const agentOptions = excludeTestAgents(employees || []).map((a) => ({
    value: a.id,
    label:
      [a.firstname, a.lastname].filter(Boolean).join(" ") || a.name || a.email || a.id,
  }));

  const clientName = text(lead.contactName) || "Listing Lead";
  const hasCoClients =
    Array.isArray(lead.coClients) && lead.coClients.some((c) => c && c.contactId);
  const headerName = hasCoClients ? clientNames(lead) : clientName;
  const stageColor = STAGE_COLORS[lead.stage] || "#94a3b8";
  const address = text(lead.propertyAddress);

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="w-full max-w-4xl max-h-[90vh] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden pointer-events-auto">
          {/* Header — same dark bar as the board's Deal Details */}
          <div className="bg-[#1E293B] text-white px-6 py-4 flex items-start justify-between shrink-0">
            <div>
              <div className="text-lg font-bold flex items-center gap-2">
                {lead.rating && (
                  <span className="text-[11px] font-bold px-1.5 py-0.5 rounded bg-white/20">
                    {lead.rating}
                  </span>
                )}
                {headerName}
              </div>
              <div className="mt-1.5 flex items-center gap-2">
                <span
                  className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full text-white"
                  style={{ backgroundColor: `${stageColor}55` }}
                >
                  {lead.stage}
                </span>
                {lead.status && lead.status !== "active" && (
                  <span className="text-xs text-slate-300 capitalize">
                    {lead.status}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 bg-white/15 hover:bg-white/25 rounded-full flex items-center justify-center transition"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          {/* Body — two columns, mirrors the board overview layout */}
          <div className="flex-1 overflow-y-auto bg-slate-50 p-4 grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-4">
            {/* LEFT */}
            <div className="flex flex-col gap-4">
              <Card label="Listing Details">
                <EditableTextRow
                  k="Property Address"
                  value={lead.propertyAddress}
                  canEdit={canManage}
                  placeholder="1423 Oak St"
                  onSave={(v) => updateListingLead(lead.id, { propertyAddress: v })}
                />
                <EditableTextRow
                  k="Estimated Value"
                  value={lead.estValue}
                  type="number"
                  canEdit={canManage}
                  display={fmtPrice}
                  placeholder="$620,000"
                  onSave={(v) => updateListingLead(lead.id, { estValue: toNum(v) })}
                />
                <EditableTextRow
                  k="List Price"
                  value={lead.listPrice}
                  type="number"
                  canEdit={canManage}
                  display={fmtPrice}
                  onSave={(v) => updateListingLead(lead.id, { listPrice: toNum(v) })}
                />
                <EditableSelectRow
                  k="Source"
                  value={lead.source}
                  canEdit={canManage}
                  options={systemSourceOptions(leadsource)}
                  display={(v) => leadSourceLabel(v, leadsource) || text(v)}
                  onSave={(v) => updateListingLead(lead.id, { source: v })}
                />
                <EditableSelectRow
                  k="Rating"
                  value={lead.rating}
                  canEdit={canManage}
                  options={[
                    { value: "A", label: "A" },
                    { value: "B", label: "B" },
                    { value: "C", label: "C" },
                  ]}
                  onSave={(v) => updateListingLead(lead.id, { rating: v })}
                />
                <EditableDateRow
                  k="CMA Date"
                  value={lead.cmaDate}
                  canEdit={canManage}
                  onSave={(v) => updateListingLead(lead.id, { cmaDate: v })}
                />
                <EditableDateRow
                  k="Listing Appt"
                  value={lead.appointmentAt}
                  canEdit={canManage}
                  onSave={(v) => updateListingLead(lead.id, { appointmentAt: v })}
                />
              </Card>

              {address && (
                <Card label="Location">
                  <div className="flex items-start gap-2">
                    <MapPin
                      size={16}
                      className="text-slate-400 mt-0.5 shrink-0"
                    />
                    <div className="text-sm text-slate-700">{address}</div>
                  </div>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-2.5 text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                  >
                    Open in Google Maps <ExternalLink size={12} />
                  </a>
                </Card>
              )}

              <Card label="Pipeline & Follow-Up">
                <EditableSelectRow
                  k="Stage"
                  value={lead.stage}
                  canEdit={canManage}
                  placeholder="New Lead"
                  options={LISTING_STAGES.map((s) => ({ value: s, label: s }))}
                  onSave={(v) =>
                    updateListingLead(lead.id, { stage: v || "New Lead" })
                  }
                />
                <EditableSelectRow
                  k="Assigned Agent"
                  value={lead.assignedAgent}
                  canEdit={canManage}
                  options={agentOptions}
                  display={() => text(lead.assignedAgentName)}
                  onSave={(v) => {
                    const a = (employees || []).find((e) => e.id === v);
                    const name = a
                      ? [a.firstname, a.lastname].filter(Boolean).join(" ") ||
                        a.name ||
                        a.email ||
                        ""
                      : "";
                    return updateListingLead(lead.id, {
                      assignedAgent: v,
                      assignedAgentName: name,
                    });
                  }}
                />
                <CoListingAgentsRow lead={lead} agents={employees} canEdit={canManage} />
                <EditableDateRow
                  k="Next Follow-Up"
                  value={lead.nextFollowUp}
                  canEdit={canManage}
                  onSave={(v) => updateListingLead(lead.id, { nextFollowUp: v })}
                />
                {(lead.tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {lead.tags.map((t) => (
                      <span
                        key={t}
                        className="text-[11px] bg-slate-100 text-slate-600 rounded-full px-2 py-0.5"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </Card>
            </div>

            {/* RIGHT */}
            <div className="flex flex-col gap-4">
              <Card label="Client">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-semibold text-sm shrink-0">
                    {initialsOf(clientName) || "—"}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-800 truncate">
                      {clientName}
                    </div>
                    {contact?.email && (
                      <div className="text-xs text-slate-500 truncate">
                        {contact.email}
                      </div>
                    )}
                    {contact?.phone && (
                      <div className="text-xs text-slate-500">
                        {contact.phone}
                      </div>
                    )}
                  </div>
                  {canManage && (
                    <button
                      onClick={() => setPrimaryPickerOpen(true)}
                      className="ml-auto shrink-0 text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                    >
                      Change
                    </button>
                  )}
                </div>
                {listClients(lead)
                  .filter((c) => !c.primary)
                  .map((c) => (
                    <div key={c.contactId} className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100 text-sm">
                      <span className="text-slate-700">{c.contactName || "(unnamed)"}</span>
                      {canManage && (
                        <button onClick={() => removeCoClient(c.contactId)}
                          className="text-xs text-slate-400 hover:text-red-500">Remove</button>
                      )}
                    </div>
                  ))}
                {canManage && (
                  <button onClick={() => setCoPickerOpen(true)}
                    className="text-sm font-semibold text-sky-600 mt-2">+ Add Co-Client</button>
                )}
              </Card>

              <Card label="Notes">
                <EditableNotes
                  value={lead.notes}
                  canEdit={canManage}
                  hideLabel
                  onSave={(notes) => updateListingLead(lead.id, { notes })}
                />
              </Card>

              <Card label="Activity">
                <div className="text-xs text-slate-600 flex flex-col gap-1.5">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    Stage: {lead.stage}
                  </div>
                  {lead.createdAt && (
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                      Created · {fmtDate(lead.createdAt)}
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </div>

          {/* Footer — same actions as before */}
          {canManage && (
            <div className="px-6 py-3 border-t border-slate-200 bg-white flex flex-wrap items-center gap-2 shrink-0">
              {lead.status === "active" && (() => {
                // Requested = a file request exists (from here or the modal).
                // A lead moved in from the board also carries sourceDealId but
                // was never "requested" — its file already exists on the board.
                const requested = requestedHere || !!lead.fileRequested;
                const hasFile = requested || !!lead.sourceDealId;
                return (
                  <button
                    onClick={handleRequestFile}
                    disabled={busy || hasFile}
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-2 disabled:opacity-60 whitespace-nowrap ${hasFile ? "bg-amber-100 text-amber-700 border border-amber-300" : "bg-slate-700 text-white"}`}
                  >
                    <FileText size={14} />{" "}
                    {requested ? "File Requested ✓" : hasFile ? "File Linked ✓" : "Request Listing File"}
                  </button>
                );
              })()}
              {lead.status === "active" && (
                <>
                  <select
                    value={formType}
                    onChange={(e) => setFormType(e.target.value)}
                    disabled={busy}
                    title="Form Type — decides which forms the seller fills out"
                    className="text-xs font-semibold border border-slate-300 text-slate-700 rounded-lg px-2 py-2 bg-white disabled:opacity-60"
                  >
                    {LISTING_FORM_TYPES.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={handleSendSellerForms}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold bg-slate-700 text-white rounded-lg px-3 py-2 disabled:opacity-60 whitespace-nowrap"
                  >
                    <Mail size={14} /> Send Seller Forms
                  </button>
                </>
              )}
              {lead.status === "active" &&
                (lead.stage === GRADUATION_STAGE ||
                  PARKING_STAGES.includes(lead.stage)) && (
                  <button
                    onClick={handleGraduate}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold bg-emerald-600 text-white rounded-lg px-4 py-2 disabled:opacity-60"
                  >
                    <CheckCircle2 size={15} /> Sign the Listing → Deals Board
                  </button>
                )}
              {/* ⋯ — negative actions. Opens upward (bottom bar). */}
              <div className="relative ml-auto">
                <button
                  onClick={() => setMenuOpen((v) => !v)}
                  aria-label="More Actions"
                  className="inline-flex items-center text-sm font-semibold border border-slate-300 text-slate-600 rounded-lg px-2.5 py-2 hover:bg-slate-50"
                >
                  <MoreVertical size={16} />
                </button>
                {menuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setMenuOpen(false)}
                    />
                    <div className="absolute right-0 bottom-full mb-1 z-20 w-44 bg-white rounded-lg shadow-xl border border-slate-200 py-1">
                      {lead.status === "active" && (
                        <button
                          onClick={() => {
                            setMenuOpen(false);
                            handleMarkLost();
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                        >
                          <Ban size={14} /> Mark as Lost
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setMenuOpen(false);
                          handleDelete();
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      >
                        <Trash2 size={14} /> Delete Lead
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {coPickerOpen && (
        <AddClientPicker
          userId={user.id}
          existingCustomerIds={listClients(lead).map((c) => c.contactId)}
          onClose={() => setCoPickerOpen(false)}
          onPick={addCoClientToLead}
          onCreateNew={() => { setCoPickerOpen(false); setCreatingCoContact(true); }}
        />
      )}
      {creatingCoContact && (
        <ContactEditDrawer
          onClose={() => setCreatingCoContact(false)}
          onSubmit={handleCreatedCoContact}
        />
      )}
      {primaryPickerOpen && (
        <AddClientPicker
          userId={user.id}
          existingCustomerIds={listClients(lead).map((c) => c.contactId)}
          onClose={() => setPrimaryPickerOpen(false)}
          onPick={changePrimary}
          onCreateNew={() => {
            setPrimaryPickerOpen(false);
            setCreatingPrimaryContact(true);
          }}
        />
      )}
      {creatingPrimaryContact && (
        <ContactEditDrawer
          onClose={() => setCreatingPrimaryContact(false)}
          onSubmit={handleCreatedPrimaryContact}
        />
      )}
    </>
  );
}
